"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@/lib/utils';
import { useAgentActions } from '@/hooks/use-agent-actions';
const renderStars = (rating, reviewCount) => {
    if (typeof rating !== 'number' || rating <= 0) {
        return null;
    }
    const resolvedRating = Math.max(0, Math.min(5, rating));
    const rounded = Math.max(0, Math.min(5, Math.round(resolvedRating)));
    return (_jsxs("div", { className: "flex items-center gap-1 text-[10px]", children: [_jsx("div", { className: "flex items-center gap-0.5 text-amber-400", children: Array.from({ length: 5 }).map((_, index) => {
                    const isActive = index < rounded;
                    return (_jsx("svg", { viewBox: "0 0 24 24", className: cn('h-2.5 w-2.5', isActive ? 'fill-current' : 'fill-gray-200 text-gray-200'), children: _jsx("path", { d: "M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" }) }, index));
                }) }), _jsx("span", { className: "font-medium text-gray-700", children: resolvedRating.toFixed(1) }), typeof reviewCount === 'number' && reviewCount > 0 && (_jsxs("span", { className: "text-gray-500", children: ["(", reviewCount.toLocaleString(), ")"] }))] }));
};
const sanitizeText = (value) => {
    if (!value)
        return '';
    // Data comes as plain JSON text, not markdown - just clean up formatting chars
    return value
        .replace(/[*_`~]+/g, ' ')
        .replace(/^[-*•]\s+/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};
export const ProductCard = ({ id, name, price, image, url, rating, reviewCount, purchaseInfo, deliveryInfo, description, }) => {
    const { sendPrompt } = useAgentActions();
    // Debug logging to track incoming data
    if (process.env.NODE_ENV === 'development') {
        console.log('[ProductCard] Received data:', { name, price, url, image, rating });
    }
    const rawName = sanitizeText(name);
    const hasReadableName = /[a-z0-9]/i.test(rawName);
    // Improved fallback: try to extract from URL if name is missing
    let displayName = rawName && rawName !== '.' && hasReadableName ? rawName : '';
    // If the name looks like a confirmation sentence ("X has been added to your cart"),
    // trim it back to just the product name prefix.
    if (displayName) {
        const addedMatch = displayName.match(/^(.+?)\s+has been (successfully )?added to your (cart|basket)\b.*$/i);
        if (addedMatch && addedMatch[1]) {
            displayName = addedMatch[1].trim();
        }
    }
    // Treat generic labels like "here" as non-names
    if (displayName && /^(?:here|this|click here|view(?: it| here)?|open|link)$/i.test(displayName)) {
        displayName = '';
    }
    if (!displayName && url) {
        // Try to extract product name from URL slug
        const slugMatch = url.match(/\/products\/([^/?#]+)/i);
        if (slugMatch) {
            displayName = decodeURIComponent(slugMatch[1]).replace(/[-_]+/g, ' ');
        }
    }
    // Final fallback
    if (!displayName) {
        displayName = 'Product';
        if (process.env.NODE_ENV === 'development') {
            console.warn('[ProductCard] Using fallback name "Product" for:', { originalName: name, url });
        }
    }
    const normalizedPrice = sanitizeText(price);
    const priceText = normalizedPrice && normalizedPrice.toLowerCase() !== 'n/a' ? normalizedPrice : '';
    const normalizedPurchaseInfo = sanitizeText(purchaseInfo);
    const normalizedDeliveryInfo = sanitizeText(deliveryInfo);
    const normalizedDescription = sanitizeText(description);
    const shouldShowPurchaseInfo = normalizedPurchaseInfo &&
        (!priceText || normalizedPurchaseInfo.toLowerCase() !== priceText.toLowerCase()) &&
        !/^price\s*[:=]/i.test(normalizedPurchaseInfo);
    const ratingDisplay = renderStars(rating, reviewCount);
    const openProduct = () => {
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };
    const handleAddToCart = (e) => {
        e.stopPropagation();
        const productLabel = displayName || 'this item';
        const promptParts = [`Please add ${productLabel} to my cart.`];
        if (priceText) {
            promptParts.push(`Price: ${priceText}.`);
        }
        // Include product ID instead of URL to prevent LLM from echoing markdown links
        if (id) {
            promptParts.push(`Product ID: ${id}`);
        }
        // DO NOT include URL - it causes LLM to create markdown links that trigger product badge rendering
        sendPrompt(promptParts.join(' '));
        console.log('Requested agent to add to cart:', productLabel);
    };
    return (_jsxs("div", { className: "group relative flex w-[220px] flex-shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md", children: [_jsx("div", { className: "flex h-[130px] w-full cursor-pointer items-center justify-center bg-gray-50 p-3", onClick: openProduct, children: image ? (
                /* eslint-disable-next-line @next/next/no-img-element */
                _jsx("img", { src: image, alt: name, className: "h-full w-full object-contain transition-transform group-hover:scale-105", loading: "lazy", onError: (event) => {
                        event.currentTarget.src =
                            'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="128" height="128"%3E%3Crect fill="%23f3f4f6" width="128" height="128"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="12" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                    } })) : (_jsx("svg", { className: "h-10 w-10 text-gray-300", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 1.5, d: "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" }) })) }), _jsxs("div", { className: "flex flex-col gap-1.5 p-3", children: [_jsx("h3", { className: "line-clamp-2 cursor-pointer text-[12px] font-medium leading-tight text-gray-900 hover:text-indigo-600", onClick: openProduct, title: displayName, children: displayName }), ratingDisplay && _jsx("div", { className: "text-gray-600", children: ratingDisplay }), _jsxs("div", { className: "mt-1 flex items-end justify-between gap-2", children: [_jsxs("div", { className: "flex flex-col gap-0.5", children: [priceText && (_jsx("span", { className: "text-[14px] font-bold text-gray-900", children: priceText })), shouldShowPurchaseInfo && (_jsx("span", { className: "text-[9px] text-gray-400 line-through", children: normalizedPurchaseInfo }))] }), _jsxs("button", { type: "button", onClick: handleAddToCart, className: "flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-indigo-700 active:scale-95", children: [_jsx("svg", { className: "h-3 w-3", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 4v16m8-8H4" }) }), "Add"] })] })] })] }));
};
